import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from 'common/components/Form/Button';
import ModalV2 from 'common/components/Modal/ModalV2';
import Icon from 'common/components/Icon';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import { Content, ButtonWrapper, CardBody, CardFooter } from './styled';

interface Props {
  open: boolean;
  isLoading?: boolean;
  title: string;
  confirmButtonTitle?: string;
  cancelButtonTitle?: string;
  children: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  type?: 'warning' | 'confirm';
  /** `card` renders the plain white dialog: alert icon, left-aligned text and a footer with the buttons. */
  appearance?: 'default' | 'card';
  id?: string;
  'data-testid'?: string;
}

const ConfirmationModal: React.FC<Props> = ({
  open,
  isLoading = false,
  onCancel,
  onConfirm,
  title,
  children,
  confirmButtonTitle,
  cancelButtonTitle,
  type = 'warning',
  appearance = 'default',
  id,
  'data-testid': dataTestid,
}) => {
  const { t } = useTranslation();

  const buttons = (
    <>
      {appearance === 'card' ? (
        <SecondaryButton disabled={isLoading} isButtonV2={true} type='button' color='white' onClick={onCancel}>
          {cancelButtonTitle ? cancelButtonTitle : t('common.cancel')}
        </SecondaryButton>
      ) : (
        <LoadingButton disabled={isLoading} isButtonV2={true} type='button' color='gray100' onClick={onCancel}>
          {cancelButtonTitle ? cancelButtonTitle : t('common.cancel')}
        </LoadingButton>
      )}
      <LoadingButton
        disabled={isLoading}
        isButtonV2={true}
        pill={appearance !== 'card'}
        loading={isLoading}
        type='button'
        color={type === 'warning' ? 'wine' : 'lightPurple'}
        onClick={onConfirm}
      >
        {confirmButtonTitle ? confirmButtonTitle : t('common.delete')}
      </LoadingButton>
    </>
  );

  if (appearance === 'card') {
    return (
      <ModalV2
        isOpen={open}
        title={title}
        appearance='card'
        titleIcon={type === 'warning' ? { node: <Icon icon='alert-fill' size={20} color='red700' />, tone: 'red' } : undefined}
        customWidth={480}
        onClose={onCancel}
        data-testid={dataTestid}
        clickOutsideClose={!isLoading}
        modalId={id || 'confirmation-modal'}
      >
        <CardBody>{children}</CardBody>
        <CardFooter>{buttons}</CardFooter>
      </ModalV2>
    );
  }

  return (
    <ModalV2
      isOpen={open}
      title={title}
      onClose={onCancel}
      data-testid={dataTestid}
      clickOutsideClose={!isLoading}
      modalId={id || 'confirmation-modal'}
    >
      <Content>
        {children}
        <ButtonWrapper>
          <LoadingButton disabled={isLoading} isButtonV2={true} type='button' color='gray100' onClick={onCancel}>
            {cancelButtonTitle ? cancelButtonTitle : t('common.cancel')}
          </LoadingButton>
          <LoadingButton
            disabled={isLoading}
            isButtonV2={true}
            loading={isLoading}
            type='button'
            color={type === 'warning' ? 'wine' : 'lightPurple'}
            onClick={onConfirm}
          >
            {confirmButtonTitle ? confirmButtonTitle : t('common.delete')}
          </LoadingButton>
        </ButtonWrapper>
      </Content>
    </ModalV2>
  );
};

export default ConfirmationModal;
